interface ShootingStar {
  x: number;
  y: number;
  speed: number;
  vx: number;
  vy: number;
  tailLength: number;
  radius: number;
  opacity: number;
  phase: 'wait' | 'fade-in' | 'active' | 'fade-out';
  fadeInTime: number;
  activeTime: number;
  fadeOutTime: number;
  waitTime: number;
  timer: number;
}

interface Point {
  x: number;
  y: number;
  originX: number;
  originY: number;
  active: number;
  closest: Point[];
  circle: Circle | null;
  tx: number;
  ty: number;
  tweenProgress: number;
  tweenDuration: number;
  tweenStartX: number;
  tweenStartY: number;
}

class Circle {
  pos: { x: number; y: number };
  radius: number;
  active: number;

  constructor(
    pos: { x: number; y: number },
    radius: number,
    private ctx: CanvasRenderingContext2D,
  ) {
    this.pos = pos;
    this.radius = radius;
    this.active = 0;
  }

  draw(): void {
    if (!this.active) return;
    this.ctx.beginPath();
    this.ctx.arc(this.pos.x, this.pos.y, this.radius, 0, 2 * Math.PI, false);
    this.ctx.fillStyle = `rgba(156,217,249,${this.active})`;
    this.ctx.fill();
  }
}

/**
 * Find the K closest points to `target` using a partial selection approach
 * instead of sorting all points (O(k*n) instead of O(n log n)).
 */
const findKClosest = (
  target: Point,
  allPoints: Point[],
  k: number,
  getDistance: (
    a: { x: number; y: number },
    b: { x: number; y: number },
  ) => number,
): Point[] => {
  const result: { point: Point; dist: number }[] = [];
  for (const p of allPoints) {
    if (p === target) continue;
    const dist = getDistance(target, p);
    if (result.length < k) {
      result.push({ point: p, dist });
    } else {
      // Find the farthest in result and replace if this one is closer
      let maxIdx = 0;
      for (let i = 1; i < result.length; i++) {
        const current = result[i];
        const maxEntry = result[maxIdx];
        if (current && maxEntry && current.dist > maxEntry.dist) maxIdx = i;
      }
      const farthest = result[maxIdx];
      if (farthest && dist < farthest.dist) {
        result[maxIdx] = { point: p, dist };
      }
    }
  }
  return result.map((r) => r.point);
};

export const initHeroCanvas = (): (() => void) => {
  const canvas = document.getElementById(
    'hero-canvas',
  ) as HTMLCanvasElement | null;
  if (!canvas) return () => {};

  const ctx = canvas.getContext('2d');
  if (!ctx) return () => {};

  const isTouchDevice = 'ontouchstart' in window;

  let width = 0;
  let height = 0;
  let points: Point[] = [];
  let animateHeader = true;
  let rafId = 0;
  const target = { x: -9999, y: -9999 }; // start off-screen so nothing lights up until mouse enters

  // Reduce shooting stars on mobile
  const SHOOTING_STAR_COUNT = isTouchDevice ? 2 : 5;
  const shootingStars: ShootingStar[] = [];

  // Grid size: fewer points on mobile for better performance
  const GRID_SIZE = isTouchDevice ? 8 : 12;

  const resetShootingStar = (s: ShootingStar): void => {
    // Spawn from a random position anywhere on screen
    s.x = Math.random() * width;
    s.y = Math.random() * height;
    // Travel in any direction (full 360°)
    const angleDeg = Math.random() * 360;
    const angleRad = (angleDeg * Math.PI) / 180;
    s.speed = 380 + Math.random() * 300;
    s.vx = Math.cos(angleRad) * s.speed;
    s.vy = Math.sin(angleRad) * s.speed;
    s.tailLength = 80 + Math.random() * 120;
    s.radius = 1.5 + Math.random() * 1.5;
    s.opacity = 0;
    s.phase = 'fade-in';
    s.fadeInTime = 0.08 + Math.random() * 0.06;
    s.activeTime = 0.25 + Math.random() * 0.35;
    s.fadeOutTime = 0.12 + Math.random() * 0.08;
    s.waitTime = 2 + Math.random() * 4; // s before next spawn
    s.timer = 0;
    // Stagger initial wait so they don't all fire at once
    s.phase = 'wait';
    s.timer = Math.random() * 5;
  };

  const initShootingStars = (): void => {
    shootingStars.length = 0;
    for (let i = 0; i < SHOOTING_STAR_COUNT; i++) {
      const s = {} as ShootingStar;
      resetShootingStar(s);
      shootingStars.push(s);
    }
  };

  const updateShootingStar = (s: ShootingStar, dt: number): void => {
    const dtS = dt / 1000;
    s.timer += dtS;

    if (s.phase === 'wait') {
      if (s.timer >= s.waitTime) {
        resetShootingStar(s);
        s.phase = 'fade-in';
        s.timer = 0;
      }
      return;
    }

    // Move
    s.x += s.vx * dtS;
    s.y += s.vy * dtS;

    if (s.phase === 'fade-in') {
      s.opacity = Math.min(1, s.timer / s.fadeInTime);
      if (s.timer >= s.fadeInTime) {
        s.phase = 'active';
        s.timer = 0;
      }
    } else if (s.phase === 'active') {
      s.opacity = 1;
      if (s.timer >= s.activeTime) {
        s.phase = 'fade-out';
        s.timer = 0;
      }
    } else if (s.phase === 'fade-out') {
      s.opacity = Math.max(0, 1 - s.timer / s.fadeOutTime);
      if (s.timer >= s.fadeOutTime) {
        s.phase = 'wait';
        s.timer = 0;
        s.waitTime = 1.2 + Math.random() * 3.5;
      }
    }
  };

  const drawShootingStar = (s: ShootingStar): void => {
    if (s.phase === 'wait' || s.opacity <= 0) return;

    // Direction unit vector (normalised)
    const len = Math.hypot(s.vx, s.vy);
    const ux = s.vx / len;
    const uy = s.vy / len;

    // Tail start (behind the head)
    const tx = s.x - ux * s.tailLength;
    const ty = s.y - uy * s.tailLength;

    const grad = ctx.createLinearGradient(tx, ty, s.x, s.y);
    grad.addColorStop(0, `rgba(156,217,249,0)`);
    grad.addColorStop(0.7, `rgba(200,235,255,${s.opacity * 0.55})`);
    grad.addColorStop(1, `rgba(255,255,255,${s.opacity})`);

    ctx.save();
    ctx.lineWidth = s.radius * 1.4;
    ctx.strokeStyle = grad;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(tx, ty);
    ctx.lineTo(s.x, s.y);
    ctx.stroke();

    // Bright head dot
    ctx.beginPath();
    ctx.arc(s.x, s.y, s.radius, 0, Math.PI * 2);
    ctx.fillStyle = `rgba(255,255,255,${s.opacity})`;
    ctx.fill();

    // Soft glow around the head
    const glow = ctx.createRadialGradient(s.x, s.y, 0, s.x, s.y, s.radius * 5);
    glow.addColorStop(0, `rgba(156,217,249,${s.opacity * 0.5})`);
    glow.addColorStop(1, `rgba(156,217,249,0)`);
    ctx.beginPath();
    ctx.arc(s.x, s.y, s.radius * 5, 0, Math.PI * 2);
    ctx.fillStyle = glow;
    ctx.fill();

    ctx.restore();
  };

  const initCanvas = (): void => {
    const parent = canvas.parentElement;
    width = parent?.clientWidth || window.innerWidth;
    height = parent?.clientHeight || window.innerHeight;

    // Cap DPR to avoid rendering millions of pixels on retina/high-DPI devices
    const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  };

  const getDistance = (
    p1: { x: number; y: number },
    p2: { x: number; y: number },
  ): number => {
    return Math.pow(p1.x - p2.x, 2) + Math.pow(p1.y - p2.y, 2);
  };

  const scheduleNextTween = (p: Point): void => {
    p.tweenStartX = p.x;
    p.tweenStartY = p.y;
    p.tx = p.originX - 50 + Math.random() * 100;
    p.ty = p.originY - 50 + Math.random() * 100;
    p.tweenProgress = 0;
    p.tweenDuration = 1000 + Math.random() * 1000; // ms
  };

  const initPoints = (): void => {
    // Skip constellation on touch devices — only render shooting stars
    if (isTouchDevice) {
      points = [];
      return;
    }

    points = [];
    const cols = GRID_SIZE;
    const rows = GRID_SIZE;
    const cellW = width / cols;
    const cellH = height / rows;

    for (let col = 0; col < cols; col++) {
      for (let row = 0; row < rows; row++) {
        const px = col * cellW + Math.random() * cellW;
        const py = row * cellH + Math.random() * cellH;
        points.push({
          x: px,
          y: py,
          originX: px,
          originY: py,
          active: 0,
          closest: [],
          circle: null,
          tx: px,
          ty: py,
          tweenProgress: 1,
          tweenDuration: 1,
          tweenStartX: px,
          tweenStartY: py,
        });
      }
    }

    // Find the 5 closest neighbours using partial selection (O(k*n) per point)
    for (const p1 of points) {
      p1.closest = findKClosest(p1, points, 5, getDistance);
    }

    // Assign circles
    for (const p of points) {
      p.circle = new Circle(p, 2 + Math.random() * 2, ctx);
      scheduleNextTween(p);
    }
  };

  // Ease in-out circ approximation
  const easeInOutCirc = (t: number): number => {
    return t < 0.5
      ? (1 - Math.sqrt(1 - 4 * t * t)) / 2
      : (Math.sqrt(1 - Math.pow(-2 * t + 2, 2)) + 1) / 2;
  };

  // Render loop
  let lastTime = 0;

  /**
   * Batch all lines with the same opacity into a single path to reduce
   * the number of beginPath/stroke calls from ~700 to ~4 (one per opacity level).
   */
  const drawAllLines = (): void => {
    // Group by opacity level
    const opacityGroups = new Map<number, Point[]>();
    for (const p of points) {
      if (!p.active) continue;
      const group = opacityGroups.get(p.active);
      if (group) {
        group.push(p);
      } else {
        opacityGroups.set(p.active, [p]);
      }
    }

    for (const [opacity, group] of opacityGroups) {
      ctx.beginPath();
      ctx.strokeStyle = `rgba(156,217,249,${opacity})`;
      for (const p of group) {
        for (const neighbour of p.closest) {
          ctx.moveTo(p.x, p.y);
          ctx.lineTo(neighbour.x, neighbour.y);
        }
      }
      ctx.stroke();
    }
  };

  const animate = (now: number): void => {
    const dt = now - lastTime;
    lastTime = now;

    if (animateHeader) {
      ctx.clearRect(0, 0, width, height);

      // Shooting stars (drawn first, behind the constellation)
      for (const s of shootingStars) {
        updateShootingStar(s, dt);
        drawShootingStar(s);
      }

      for (const p of points) {
        // Advance tween
        p.tweenProgress = Math.min(1, p.tweenProgress + dt / p.tweenDuration);
        const e = easeInOutCirc(p.tweenProgress);
        p.x = p.tweenStartX + (p.tx - p.tweenStartX) * e;
        p.y = p.tweenStartY + (p.ty - p.tweenStartY) * e;
        if (p.tweenProgress >= 1) scheduleNextTween(p);

        // Activity based on distance to mouse
        const dist = getDistance(target, p);
        if (dist < 4000) {
          p.active = 0.3;
          p.circle!.active = 0.6;
        } else if (dist < 20000) {
          p.active = 0.1;
          p.circle!.active = 0.3;
        } else if (dist < 40000) {
          p.active = 0.02;
          p.circle!.active = 0.1;
        } else {
          p.active = 0;
          p.circle!.active = 0;
        }

        p.circle!.draw();
      }

      // Batch all line drawing into grouped paths
      drawAllLines();
    }

    rafId = requestAnimationFrame(animate);
  };

  // Event handlers (stored as named functions for cleanup)
  const onMouseMove = (e: MouseEvent): void => {
    target.x = e.pageX;
    target.y = e.pageY;
  };

  const onScroll = (): void => {
    animateHeader = document.body.scrollTop <= height;
  };

  const onResize = (): void => {
    initCanvas();
    initPoints();
    initShootingStars();
  };

  if (!isTouchDevice) {
    window.addEventListener('mousemove', onMouseMove);
  }

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onResize, { passive: true });

  // Kick off
  initCanvas();
  initPoints();
  initShootingStars();
  rafId = requestAnimationFrame((now: number) => {
    lastTime = now;
    animate(now);
  });

  // Return cleanup function
  return () => {
    cancelAnimationFrame(rafId);
    window.removeEventListener('mousemove', onMouseMove);
    window.removeEventListener('scroll', onScroll);
    window.removeEventListener('resize', onResize);
  };
};
